import useBasket from '../basket/useBasket';
import useMobileApp from '../mobile-app/useMobileApp';
import useOrderFlow from './useOrderFlow';

interface Options {
  /**
   * The calling screen opens the order-method modal for an incomplete delivery address, so
   * it can recover that failure in place instead of showing the generic error page. Only
   * the confirmation screen can — and only outside the native app, where that modal is
   * suppressed.
   */
  recoversDeliveryDetails?: boolean;
  /**
   * The calling screen renders CustomerInfoSection / InvoiceInfoSection, which show a draft's
   * `customer_*` / `invoice_email` rejections as inline errors under the input the customer is
   * typing into. Replacing that form with a full-screen error hides the field they have to fix.
   */
  recoversCustomerInput?: boolean;
}

interface DraftError {
  /** Render the error page instead of this screen's normal content. */
  show: boolean;
  /** The failure is a missing or rejected delivery address, which needs its own copy. */
  isDeliveryDetails: boolean;
}

/**
 * Single source of truth for "this screen cannot render, and no draft is coming".
 *
 * The predicate has to be identical on a screen and on the CTA footer that sits beside it
 * (the Ionic pages render the two as siblings), so it lives here rather than being spelled
 * out at each of the four call sites — where the two mobile copies had already drifted from
 * the two web ones.
 */
export default function useDraftError(options: Options = {}): DraftError {
  const { recoversDeliveryDetails = false, recoversCustomerInput = false } = options;

  const { data, draftFailed, draftFailureReason } = useOrderFlow();
  const { items } = useBasket();
  const { isMobileApp } = useMobileApp();

  const isDeliveryDetails = draftFailureReason === 'delivery_details';

  // OrderConfirmationScreen's address-modal effect early-returns on `isMobileApp`, so in the
  // native app there is no in-place recovery to defer to and the error page is the only way
  // out of the loader. The inline field errors have no such caveat — the form renders them on
  // every platform.
  const recoveredInPlace =
    (isDeliveryDetails && recoversDeliveryDetails && !isMobileApp) ||
    (draftFailureReason === 'customer_input' && recoversCustomerInput);

  return {
    // An empty basket has its own illustration on both screens, and the draft effect is
    // gated on `items.length` — so a failure latched before the basket was emptied would
    // otherwise leave an error page that "Try again" can never clear.
    show: draftFailed && !data.id && items.length > 0 && !recoveredInPlace,
    isDeliveryDetails,
  };
}
